/**
 * Referral link helpers.
 *
 * createReferralLink: POST /referrals/from-share to append ?ref= to a share URL.
 * attributeReferral: POST /referrals/attribute to link current user to a referral code.
 * Preferences key: "pending_referral_code" — survives navigation + sign-up flow.
 */
import { Preferences } from "@capacitor/preferences";
import { api } from "@/lib/api";

const PREFS_KEY = "pending_referral_code";

export interface ReferralCreateResponse {
  referral_code: string;
  share_url: string;
}

export async function createReferralLinkForToken(
  shareToken: string,
): Promise<ReferralCreateResponse> {
  return api<ReferralCreateResponse>(
    `/api/v1/referrals/from-share?share_token=${encodeURIComponent(shareToken)}`,
    { method: "POST" },
  );
}

export async function attributeReferral(referralCode: string): Promise<void> {
  await api("/api/v1/referrals/attribute", {
    method: "POST",
    body: { referral_code: referralCode },
  });
}

export async function savePendingReferralCode(code: string): Promise<void> {
  await Preferences.set({ key: PREFS_KEY, value: code });
}

export async function getPendingReferralCode(): Promise<string | null> {
  const { value } = await Preferences.get({ key: PREFS_KEY });
  return value ?? null;
}

export async function removePendingReferralCode(): Promise<void> {
  await Preferences.remove({ key: PREFS_KEY });
}

/** @deprecated Use getPendingReferralCode + removePendingReferralCode to ensure
 *  the code is only deleted after successful attribution. */
export async function consumePendingReferralCode(): Promise<string | null> {
  const { value } = await Preferences.get({ key: PREFS_KEY });
  if (value) {
    await Preferences.remove({ key: PREFS_KEY });
  }
  return value ?? null;
}
